import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Checkbox,
  createStyles,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  withStyles,
} from '@material-ui/core';

import BaseDialog from './baseDialog';

import {IMG_URL, KOJI_EXAMINATIONS, MODEL_UNIT_PRICE_CATEGORIES} from '../data';
import {Field, getFormValues, reduxForm} from 'redux-form';
import {connect} from 'react-redux';

/** ポジティブボタンで使用するパレット */
// const palette = CommonTheme.palette.primary;

/** 画面で使用するチェックボックス */
export const NoPaddingCheckbox = withStyles((theme) => createStyles({
  root: {
    'padding': '0',
  },
}))(Checkbox);

/** 下部のみRoundのPaper */
const BottomRoundPaper = withStyles((theme) => createStyles({
  root: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
}))(Paper);

/**
 * モデル単価チェックボックスのredux-form用レンダー処理
 * @param {object} props
 * @param {object} props.input - redux-formのinputオブジェクト
 * @param {boolean} props.disabled - チェック不可にするかどうか
 * @return {JSX.Element} - チェックボックスコンポーネント
 */
const renderKojiCheckbox = ({input, disabled}) => (
  <NoPaddingCheckbox
    color="primary"
    checked={!!input.value}
    onChange={input.onChange}
    disabled={disabled}
  />
);

const mapStateToProps = (state) => ({
});

const mapDispatchToProps = {
};

const styles = (theme) => ({
  kojiItem: {
    height: '100%',
  },
  kojiItemDisabled: {
    height: '100%',
    backgroundColor: theme.palette.action.disabledBackground,
    pointerEvents: 'none', // クリック無効化
    opacity: 0.6,
  },
});

const KojiEditDialog = (props) => {
  const {
    selectedDntList,
    disabled,
    readOnly,
    classes,
    onOk,
    values,
    kojiTypeOverviewGroups,
    open,
    initialize,
    initialValues,
    onCancel,
    maxWidth,
    fullWidth,
  } = props;

  // ダイアログを開くたびに、確実にフォームをinitialValuesで再初期化
  React.useEffect(() => {
    if (open) {
      initialize(initialValues);
    }
  }, [open, initialize, initialValues]);

  // 現在チェックされている項目一覧
  const checkedTypes = Object.keys(KOJI_EXAMINATIONS).filter(
      (t) => !!values?.[t]);

  // 項目(モデル単価)の工事内容グループ(汎用マスタ「DrSideKoziType」のValueString2__c)を取得する
  const getOverviewGroup = (t) => {
    const info = MODEL_UNIT_PRICE_CATEGORIES.find((c) => c.types.includes(t));
    return kojiTypeOverviewGroups?.[info?.kojiType];
  };

  return (
    <BaseDialog
      selectedDntList={selectedDntList}
      // 入力エリアが縦に長いため、対象電柱一覧もダイアログの高さいっぱいまで表示する
      dntListMaxHeight="calc(100vh - 200px)"
      disabled={disabled}
      readOnly={readOnly}
      open={open}
      onOk={() => {
        onOk(values);
      }}
      onCancel={onCancel}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <Box>
        <Grid container spacing={0}>
          {Object.keys(KOJI_EXAMINATIONS).map((type) => {
            const examinationItem = KOJI_EXAMINATIONS[type];
            const examinations = examinationItem?.examination ?? [];

            const category = MODEL_UNIT_PRICE_CATEGORIES.find(
                (info) => info.types.includes(type));
            const typeLabel = category.types.indexOf(type) === 0 ? category?.headerLabel ?? '' : '　';
            const typeColor = category?.color;

            // 同じ工事分類(kojiType)の他の項目がチェックされている場合、チェック不可にする
            const sameKojiTypeTypes = MODEL_UNIT_PRICE_CATEGORIES
                .filter((info) => info.kojiType === category.kojiType)
                .flatMap((info) => info.types);

            const isSameKojiTypeChecked = sameKojiTypeTypes.some(
                (otherType) => otherType !== type && !!values?.[otherType]);

            // 自分と異なる工事内容グループの項目がチェックされている場合、チェック不可にする
            const overviewGroup = getOverviewGroup(type);
            const isDifferentOverviewGroupChecked = overviewGroup ?
              checkedTypes.some(
                  (checkedType) => checkedType !== type &&
                  getOverviewGroup(checkedType) &&
                  getOverviewGroup(checkedType) !== overviewGroup) : false;

            const isCheckDisabled = isSameKojiTypeChecked ||
              isDifferentOverviewGroupChecked;

            const className = isCheckDisabled ?
              classes.kojiItemDisabled : classes.kojiItem;

            return (
              <Grid item key={type} style={{width: '20%'}}>

                <TableContainer
                  component={BottomRoundPaper}
                  className={className}
                >
                  <Table
                    size="small"
                    style={{tableLayout: 'fixed', width: '100%'}}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          style={{
                            paddingTop: 0,
                            paddingLeft: 6,
                            paddingBottom: 0,
                            backgroundColor: typeColor,
                            fontWeight: 'bold',
                            borderBottom: 'none',
                            // color: 'rgba(0, 0, 0, 0.4)',
                          }}>
                          {typeLabel}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          width="65%"
                          style={{
                            paddingTop: 0,
                            backgroundColor: typeColor,
                            fontWeight: 'bold',
                            // color: 'rgba(0, 0, 0, 0.4)',
                          }}>
                          {type}
                        </TableCell>

                        <TableCell style={{
                          paddingTop: 0,
                          textAlign: 'right',
                          paddingRight: '6px',
                          backgroundColor: typeColor,
                        }}>
                          <Field
                            name={type}
                            component={renderKojiCheckbox}
                            disabled={isCheckDisabled || readOnly}
                          />
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={2} style={{
                          // padding: '6px',
                        }}>
                          {examinations.map((text, index) => (
                            <Typography key={`${type}-${index}`} variant="body2">
                              {text}
                            </Typography>
                          ))}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={2} style={{
                          borderBottom: 'none',
                          // padding: '6px',
                        }}>
                          <img className="help_image_alt" src={`${IMG_URL}/${type}.png`} alt={type} />
                        </TableCell>
                      </TableRow>

                    </TableBody>
                  </Table>
                </TableContainer>

              </Grid>
            );
          })}
        </Grid>
      </Box>
    </BaseDialog>
  );
};

KojiEditDialog.propTypes = {
  selectedDntList: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  onOk: PropTypes.func.isRequired,
  values: PropTypes.object,
  kojiTypeOverviewGroups: PropTypes.object,
  open: PropTypes.bool,
  initialize: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  maxWidth: PropTypes.string,
  fullWidth: PropTypes.bool,
};

const FORM_NAME = 'KojiEditDialogForm';

const FormedTemplate = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: true,
  enableReinitialize: true,
})(connect((state) => {
  return {
    values: getFormValues(FORM_NAME)(state),
  };
})(KojiEditDialog));

export default withStyles(styles)(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(FormedTemplate),
);
